import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { CloudSun, Wind, Droplets as Drop, ThermometerSun, MapPin } from "lucide-react";

export default function PortalWeather() {
  const [stations, setStations] = useState(null);
  useEffect(() => { api.get("/portal/weather").then(({ data }) => setStations(data)).catch(() => setStations([])); }, []);
  if (!stations) return <div className="p-6 font-bold uppercase text-sm">Loading weather…</div>;
  if (!stations.length) return <div className="p-6 text-stone-500 text-sm">Weather station data is temporarily unavailable.</div>;
  return (
    <div className="space-y-4" data-testid="portal-weather">
      {stations.map((s, i) => {
        const c = s.current || {};
        const days = s.daily || [];
        return (
          <div key={s.station_id || i} className="bg-white border border-stone-200" data-testid={`weather-station-${i}`}>
            <div className="bg-[#1F3D34] text-white px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="font-bold uppercase text-xs flex items-center gap-2"><CloudSun size={15} /> {s.ranch_number ? `${s.ranch_number} — ` : ""}{s.station} ({s.station_id})</div>
              <div className="text-xs text-white/70">{s.source}</div>
            </div>
            <div className="px-4 py-2 border-b border-stone-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500" data-testid={`weather-location-${i}`}>
              <MapPin size={13} className="text-[#7C9673] shrink-0" />
              <span>Nearest public station{s.station_distance_mi != null ? ` · ${s.station_distance_mi} mi from vineyard` : ""}</span>
              {s.latitude != null && <span className="font-mono">{s.latitude.toFixed(4)}, {s.longitude.toFixed(4)}</span>}
              {s.latitude != null && (
                <a data-testid={`weather-map-link-${i}`} href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`} target="_blank" rel="noreferrer"
                  className="font-bold uppercase text-[10px] text-[#1F3D34] underline">View on map</a>
              )}
            </div>
            <div className="p-4 flex flex-wrap gap-6 border-b border-stone-100 items-center">
              <Stat icon={ThermometerSun} label="Now" v={c.temp_f != null ? `${c.temp_f}°F` : "—"} />
              <Stat icon={Drop} label="Humidity" v={c.humidity != null ? `${c.humidity}%` : "—"} />
              <Stat icon={Wind} label="Wind" v={c.wind_mph != null ? `${c.wind_mph} mph` : "—"} />
              {c.description && <div className="text-sm font-semibold text-stone-600">{c.description}</div>}
              {c.observed_at && <div className="text-[10px] text-stone-400 ml-auto">observed {new Date(c.observed_at).toLocaleString()}</div>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-100">
                  <th className="p-3">Day</th><th className="p-3">Forecast</th><th className="p-3 text-right">High</th>
                  <th className="p-3 text-right">Low</th><th className="p-3 text-right">Rain %</th><th className="p-3 text-right">Wind</th>
                </tr></thead>
                <tbody>
                  {days.map((dy) => (
                    <tr key={dy.date} className="border-b border-stone-50">
                      <td className="p-3 font-semibold text-xs whitespace-nowrap">{dy.name || dy.date}</td>
                      <td className="p-3 text-xs text-stone-500">{dy.short || "—"}</td>
                      <td className="p-3 text-right font-bold">{dy.high != null ? `${dy.high}°` : "—"}</td>
                      <td className="p-3 text-right text-stone-500">{dy.low != null ? `${dy.low}°` : "—"}</td>
                      <td className="p-3 text-right">{dy.precip_chance != null ? `${dy.precip_chance}%` : "—"}</td>
                      <td className="p-3 text-right whitespace-nowrap">{dy.wind || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Stat = ({ icon: I, label, v }) => (
  <div className="flex items-center gap-2">
    <I size={18} className="text-[#7C9673]" />
    <div><div className="text-[9px] font-bold uppercase text-stone-400">{label}</div><div className="font-bold">{v}</div></div>
  </div>
);
